"use client";

import Image from "next/image";
import { useState } from "react";
import ScrollReveal from "../common/ScrollReveal";
import BarHeading from "../common/BarHeading";
import TabbedContent from "../common/TabbedContent";

type Section = "khaicode" | "ai" | "fuzzing" | "admin";

interface Engine {
  title: string;
  description: string;
}

// Figma 1808:41857 — "Three Engines, Complete Coverage"
const engines: Engine[] = [
  {
    title: "KhaiCode Engine",
    description:
      "Built in collaboration with KhaiCode Binary Exploit Intelligence Platform, this engine automates the discovery and exploitation of known vulnerabilities in compiled binaries with no source code required. It decomposes software components, maps them against CVE records, traces exploitable attack paths, and validates exploitability, giving results from scan to confirmed exploit in hours.",
  },
  {
    title: "AI-Powered Vulnerability Research",
    description:
      "An AI-orchestrated engine that drives the complete vulnerability discovery and exploitation workflow. It maps the attack surface, coordinates fuzzing and analysis in parallel, provisions lab environments automatically, and guides exploit development. Research cycles that used to take weeks now take days.",
  },
  {
    title: "Fuzzing Management Platform",
    description:
      "A managed fuzzing environment that takes the operational burden off research teams. Teams configure and launch campaigns in minutes, scale to hundreds of parallel instances automatically, and receive deduplicated, classified results that are ready for review, not raw output to sort through.",
  },
];

function CapabilityBullets({ items }: { items: string[] }) {
  return (
    <ul className="flex flex-col gap-1">
      {items.map((item, i) => (
        <li key={i} className="flex items-start gap-4">
          <span aria-hidden className="shrink-0 mt-[10px] block h-2 w-2 rounded-full bg-primary" />
          {/* Justified text is only readable at desktop measure; mobile stays
              left-aligned to avoid rivers of white space. */}
          <p className="flex-1 min-w-0 text-text-highlight text-[14px] sm:text-[15px] lg:text-[16px] leading-[24px] sm:leading-[26px] lg:leading-[28px] tracking-[0.5px] text-left lg:text-justify">
            {item}
          </p>
        </li>
      ))}
    </ul>
  );
}

// Figma 1808:41900 — Core Capabilities tab content.
const coreCapabilities = [
  {
    id: "khaicode",
    title: "KhaiCode Engine",
    content: (
      <div className="flex flex-col gap-2">
        {/* Intro spans full width (matches design). */}
        <p className="text-text-highlight text-[14px] sm:text-[15px] lg:text-[16px] leading-[24px] sm:leading-[26px] lg:leading-[28px] tracking-[0.5px]">
          Software deployments commonly include third-party components with known, unpatched
          vulnerabilities that haven&apos;t been reviewed. SHRED integrates KhaiCode Binary Exploit
          Intelligence Platform to automates the process of finding and validating them in compiled
          binaries — systematically, at scale, without source code.
        </p>
        {/* Bullets (left) | collaboration logo (right, top-aligned per Figma).
            On mobile the logo drops below the bullets, centred under a divider. */}
        <div className="flex flex-col gap-6 lg:flex-row lg:gap-12 lg:items-start">
          <div className="flex-1 min-w-0">
            <CapabilityBullets
              items={[
                "Binary Software Composition Analysis: decomposes binaries, identifies embedded components, and maps them against known CVE records.",
                "Attack Path Determination: traces exploitable paths within the target and assesses real-world impact for each finding.",
                "Exploit Validation: uses an exploit template database and automated generator to confirm exploitability and deliver a validated proof of concept.",
              ]}
            />
          </div>
          <div className="flex shrink-0 flex-col items-center gap-3 border-t border-white/10 pt-6 lg:gap-5 lg:border-0 lg:pt-0">
            {/* Leading follows the text down: 28px under 14px copy is a 2.0
                ratio on phones. */}
            <p className="text-text-highlight text-[14px] sm:text-[15px] lg:text-[16px] leading-[22px] sm:leading-[28px] tracking-[0.5px] whitespace-nowrap">
              In collaboration with
            </p>
            <Image
              src="/images/Company/khaicode.svg"
              alt="KhaiCode"
              width={242}
              height={35}
              className="h-[28px] lg:h-[35px] w-auto"
            />
          </div>
        </div>
      </div>
    ),
  },
  {
    id: "ai",
    title: ["AI-Powered", "Vulnerability Research"],
    content: (
      <p className="text-text-highlight text-[14px] sm:text-[15px] lg:text-[16px] leading-[24px] sm:leading-[26px] lg:leading-[28px] tracking-[0.5px]">
        {engines[1].description}
      </p>
    ),
  },
  {
    id: "fuzzing",
    title: ["Fuzzing Management", "Platform"],
    content: (
      <p className="text-text-highlight text-[14px] sm:text-[15px] lg:text-[16px] leading-[24px] sm:leading-[26px] lg:leading-[28px] tracking-[0.5px]">
        {engines[2].description}
      </p>
    ),
  },
  {
    id: "admin",
    title: "Platform Administration",
    content: (
      <p className="text-text-highlight text-[14px] sm:text-[15px] lg:text-[16px] leading-[24px] sm:leading-[26px] lg:leading-[28px] tracking-[0.5px]">
        Centralized administration for the SHRED platform — manage the metrics dashboard for
        program-wide visibility, projects for source code and binary targets, input libraries for
        corpora and grammar files, and the infrastructure integration settings that connect SHRED to
        your existing security tooling.
      </p>
    ),
  },
];

export default function ShredProduct() {
  const [activeTab, setActiveTab] = useState<Section>("khaicode");

  return (
    <section className="relative w-full px-4 sm:px-6 lg:px-8 pt-2 pb-16 overflow-hidden">
      <div className="max-w-[1300px] mx-auto flex flex-col gap-12 md:gap-12 lg:gap-12">
        {/* Part 1: intro + Three Engines (left) | SHRED wordmark (right) */}
        <div className="grid grid-cols-1 lg:grid-cols-[745fr_542fr] gap-4 lg:gap-0 items-start">
          <ScrollReveal direction="up" delay={0}>
            <div className="flex flex-col gap-6">
              {/* One blank line between paragraphs, same as every other body
                  block (see OTSecurity.tsx). Without a gap here the two <p>
                  ran straight into each other and read as a single block. */}
              <div className="flex flex-col gap-6 sm:gap-7 text-text-highlight text-[14px] sm:text-[15px] lg:text-[16px] leading-[24px] sm:leading-[26px] lg:leading-[28px] tracking-[0.5px]">
                <p>
                  SHRED is a centralized vulnerability research platform that enables security teams
                  to manage fuzzing campaigns, configure research engines, triage crash findings,
                  and track vulnerabilities from discovery through remediation, all from a single
                  interface.
                </p>
                <p>
                  The platform includes a metrics dashboard for program-wide visibility, a project
                  manager for source code and binary targets, an input library for corpora and
                  grammar files, and infrastructure integration settings that connect SHRED to your
                  existing security tooling.
                </p>
              </div>

              <div className="flex flex-col gap-4">
                {/* The heading used to be a bold <span> sharing a <p> with the
                    sentence below it, which made it read as a lead-in rather
                    than a section heading. It is now the shared BarHeading, the
                    same rank the OT Cyber Range page uses, and the sentence
                    stands as its own paragraph. */}
                <BarHeading>Three Engines, Complete Coverage</BarHeading>
                <p className="text-text-highlight text-[14px] sm:text-[15px] lg:text-[16px] leading-[24px] sm:leading-[26px] lg:leading-[28px] tracking-[0.5px]">
                  Three specialized engines cover the full vulnerability landscape:
                </p>

                <div className="flex flex-col gap-8">
                  {engines.map((engine) => (
                    <div key={engine.title} className="flex flex-col gap-2">
                      <p className="font-[family-name:var(--font-avenir)] font-semibold text-primary text-[14px] sm:text-[16px] leading-[22px] sm:leading-[24px] tracking-[0.5px]">
                        {engine.title}
                      </p>
                      <p className="text-text-highlight text-[14px] sm:text-[15px] lg:text-[16px] leading-[24px] sm:leading-[26px] lg:leading-[28px] tracking-[0.5px] text-justify">
                        {engine.description}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </ScrollReveal>

          {/* On mobile the product visual leads (order-first) instead of being
              pushed below the intro/engine text; on lg it returns to the
              right-hand column (source order). */}
          <ScrollReveal direction="right" delay={150} className="order-first lg:order-none">
            <div className="relative w-full max-w-[542px] mx-auto lg:ml-auto flex items-start justify-center pt-2 lg:pt-4">
              <Image
                src="/images/Home/shred.svg"
                alt="SHRED — unified vulnerability research platform"
                width={450}
                height={150}
                className="relative w-full max-w-[450px] h-auto"
                priority
              />
            </div>
          </ScrollReveal>
        </div>

        {/* Core Capabilities — tabbed panel (Figma 1808:41900) */}
        <div className="flex flex-col gap-6">
          <ScrollReveal direction="up" delay={0}>
            <BarHeading>Core Capabilities</BarHeading>
          </ScrollReveal>

          <TabbedContent
            items={coreCapabilities}
            currentTab={activeTab}
            onTabChange={(id) => setActiveTab(id as Section)}
          />
        </div>
      </div>
    </section>
  );
}
